/**
 * STRIPE PRICE REGISTRY
 *
 * Ce fichier centralise tous les price IDs Stripe pour éviter de modifier
 * les webhooks et create-payment-intent à chaque changement de prix.
 *
 * ============================================================================
 * QUAND TU CHANGES UN PRIX :
 * ============================================================================
 * 1. Crée le nouveau prix dans Stripe Dashboard
 * 2. Ajoute la nouvelle variable d'environnement dans next.config.mjs
 * 3. Déplace l'ancien `current` dans le tableau `legacy` ci-dessous
 * 4. Mets le nouveau price ID (via process.env.TA_NOUVELLE_VARIABLE) dans `current`
 * 5. Met à jour le prix affiché dans next.config.mjs (ex: BELIEVEMY_PREMIUM_ANNUALLY_PRICE)
 * ============================================================================
 */

/**
 * Périodes d'engagement en secondes.
 * Utilise ces constantes au lieu de valeurs numériques pour plus de lisibilité.
 */
export const ENGAGEMENT_PERIODS = {
    NONE: null,
    ONE_MONTH: 2592000, // 30 jours
    THREE_MONTHS: 7776000, // 90 jours
    SIX_MONTHS: 15768000, // ~182.5 jours
    TWELVE_MONTHS: 31536000, // 365 jours
};

/**
 * Registry des prix Stripe avec engagement par price ID.
 *
 * Structure :
 * - current : { priceId, engagement } - Le prix actuel pour les nouvelles souscriptions
 * - legacy : [{ priceId, engagement }] - Les anciens prix pour les renouvellements
 *
 * engagement = durée pendant laquelle l'utilisateur ne peut PAS résilier (en secondes)
 *            = null si résiliation possible immédiatement (effet à la fin de la période)
 */
export const STRIPE_PRICES = {
    premium: {
        monthly: {
            current: {
                // Grille d'avant l'été rétablie le 18/09/2026 : la grille de
                // juillet (39€ sans engagement + 288€ d'un coup) avait fait
                // chuter les ventes, l'année en 12 fois étant la seule offre
                // qui se vendait vraiment.
                priceId: process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE_STRIPE_2025, // 79€/month
                engagement: ENGAGEMENT_PERIODS.NONE,
            },
            legacy: [
                {
                    priceId:
                        process.env
                            .BELIEVEMY_PREMIUM_MONTHLY_PRICE_STRIPE_JULY_2026, // 39€/month sans engagement (grille juillet 2026)
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
                {
                    priceId:
                        process.env
                            .BELIEVEMY_PREMIUM_MONTHLY_PRICE_STRIPE_FEBRUARY_2026, // 49€
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
                {
                    priceId:
                        process.env
                            .BELIEVEMY_PREMIUM_MONTHLY_PRICE_STRIPE_JANUARY_2026, // 95€
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
                {
                    priceId:
                        process.env
                            .BELIEVEMY_PREMIUM_MONTHLY_PRICE_STRIPE_AUGUST_2025,
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
                {
                    priceId:
                        process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE_STRIPE_2024,
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
                {
                    priceId: process.env.BELIEVEMY_PREMIUM_MONTHLY_PRICE_STRIPE, // 49,99 €/month
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
                {
                    priceId: process.env.BELIEVEMY_PRO_MONTHLY_STRIPE,
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
            ],
        },
        annually: {
            current: {
                priceId:
                    process.env
                        .BELIEVEMY_PREMIUM_PAID_ANNUALLY_FEBRUARY_2026_PRICE, // 29€/month
                engagement: ENGAGEMENT_PERIODS.TWELVE_MONTHS, // Mensualisé avec engagement 12 mois
            },
            legacy: [
                {
                    priceId:
                        process.env.BELIEVEMY_PREMIUM_PAID_ANNUALLY_JULY_2026_PRICE, // 288€/an prépayé (soit 24€/mois) - grille juillet 2026
                    engagement: ENGAGEMENT_PERIODS.NONE, // Payé d'avance : résiliable, pas de mensualisation
                },
                {
                    priceId:
                        process.env
                            .BELIEVEMY_PREMIUM_PAID_ANNUALLY_JULY_2025_PRICE, // 29€/month - 348€ paid annually
                    engagement: ENGAGEMENT_PERIODS.NONE, // Payé d'avance, pas d'engagement
                },
                {
                    priceId:
                        process.env
                            .BELIEVEMY_PREMIUM_ANNUALLY_PRICE_STRIPE_2025, // 29€/month
                    engagement: ENGAGEMENT_PERIODS.TWELVE_MONTHS, // Mensualisé avec engagement 12 mois
                },
                {
                    priceId:
                        process.env
                            .BELIEVEMY_PREMIUM_ANNUALLY_PRICE_STRIPE_2024, // 25€/month
                    engagement: ENGAGEMENT_PERIODS.TWELVE_MONTHS,
                },
                {
                    priceId:
                        process.env.BELIEVEMY_PREMIUM_ANNUALLY_PRICE_STRIPE, // 24,99 €/month
                    engagement: ENGAGEMENT_PERIODS.TWELVE_MONTHS,
                },
                {
                    priceId: process.env.BELIEVEMY_PRO_ANNUALLY_STRIPE, // 17,99 €/month
                    engagement: ENGAGEMENT_PERIODS.TWELVE_MONTHS,
                },
                {
                    priceId: process.env.BELIEVEMY_PRO_ANNUALLY_OLD, // 14,99 €/month
                    engagement: ENGAGEMENT_PERIODS.TWELVE_MONTHS,
                },
                {
                    priceId: process.env.BELIEVEMY_PRO_ANNUALLY_OLDBIS, // 179,88 €/year - 14,99 €/month
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
            ],
        },
        sixMonths: {
            current: {
                priceId:
                    process.env.BELIEVEMY_PREMIUM_SIX_MONTHS_PRICE_STRIPE_2025, // 49€/month
                engagement: ENGAGEMENT_PERIODS.SIX_MONTHS,
            },
            legacy: [
                {
                    priceId:
                        process.env
                            .BELIEVEMY_PREMIUM_SIX_MONTHS_PRICE_STRIPE_2024, // 35€/month
                    engagement: ENGAGEMENT_PERIODS.SIX_MONTHS,
                },
                {
                    priceId:
                        process.env.BELIEVEMY_PREMIUM_6_MONTHS_PRICE_STRIPE,
                    engagement: ENGAGEMENT_PERIODS.SIX_MONTHS,
                },
            ],
        },
        africaMonthly: {
            current: {
                priceId:
                    process.env.BELIEVEMY_PREMIUM_AFRICA_MONTHLY_PRICE_STRIPE, // 50€/mois, sans engagement
                engagement: ENGAGEMENT_PERIODS.NONE,
            },
            legacy: [],
        },
        africaCommitted: {
            current: {
                priceId:
                    process.env.BELIEVEMY_PREMIUM_AFRICA_COMMITTED_PRICE_STRIPE, // 15€/mois, engagement 12 mois
                engagement: ENGAGEMENT_PERIODS.TWELVE_MONTHS,
            },
            legacy: [],
        },
    },
    ultra: {
        monthly: {
            current: {
                priceId: process.env.BELIEVEMY_ULTRA_PRICE_STRIPE_JULY_2026, // 99€/mois (Founder, juillet 2026)
                engagement: ENGAGEMENT_PERIODS.NONE,
            },
            legacy: [
                {
                    priceId:
                        process.env.BELIEVEMY_ULTRA_PRICE_STRIPE_JANUARY_2026, // 99€ (ancien produit)
                    engagement: ENGAGEMENT_PERIODS.NONE,
                },
            ],
        },
    },
    // Essential : offre dediee Reps (energie illimitee). Active le flag `essential`.
    essential: {
        monthly: {
            current: {
                priceId: process.env.BELIEVEMY_ESSENTIAL_PRICE_STRIPE_2026, // 4,99€/mois
                engagement: ENGAGEMENT_PERIODS.NONE,
            },
            legacy: [],
        },
        annually: {
            current: {
                priceId:
                    process.env.BELIEVEMY_ESSENTIAL_ANNUALLY_PRICE_STRIPE_2026, // 39,99€/an (3,33€/mois)
                engagement: ENGAGEMENT_PERIODS.NONE,
            },
            legacy: [],
        },
    },
};

// ============================================================================
// FONCTIONS UTILITAIRES
// ============================================================================

/**
 * Trouve les informations d'un price ID dans le registry.
 *
 * @param {string} priceId - Le price ID Stripe
 * @returns {{ type: string, cycle: string, engagement: number | null } | null}
 */
function findPriceInfo(priceId) {
    for (const [type, cycles] of Object.entries(STRIPE_PRICES)) {
        for (const [cycle, { current, legacy }] of Object.entries(cycles)) {
            if (priceId === current.priceId) {
                return { type, cycle, engagement: current.engagement };
            }
            const legacyItem = legacy.find((item) => item.priceId === priceId);
            if (legacyItem) {
                return { type, cycle, engagement: legacyItem.engagement };
            }
        }
    }
    return null;
}

/**
 * Trouve le type d'abonnement à partir d'un price ID Stripe.
 * Utilisé par les webhooks pour identifier les renouvellements (current + legacy).
 *
 * @param {string} priceId - Le price ID Stripe
 * @returns {{ type: string, cycle: string } | null}
 */
export function getSubscriptionType(priceId) {
    const info = findPriceInfo(priceId);
    if (!info) return null;
    return { type: info.type, cycle: info.cycle };
}

/**
 * Récupère le price ID actuel pour créer une nouvelle souscription.
 *
 * @param {string} type - Le type d'abonnement (premium, ultra)
 * @param {string} cycle - Le cycle de facturation (monthly, annually, sixMonths)
 * @returns {string | undefined}
 */
export function getCurrentPriceId(type, cycle) {
    return STRIPE_PRICES[type]?.[cycle]?.current?.priceId;
}

/**
 * Vérifie si un price ID appartient à un type d'abonnement spécifique.
 *
 * @param {string} priceId - Le price ID Stripe
 * @param {string} type - Le type d'abonnement à vérifier (premium, ultra)
 * @returns {boolean}
 */
export function isPriceOfType(priceId, type) {
    const result = getSubscriptionType(priceId);
    return result?.type === type;
}

/**
 * Retourne la clé de traduction pour le nom d'affichage d'un abonnement.
 *
 * @param {string} priceId - Le price ID Stripe
 * @returns {string | null}
 */
export function getSubscriptionDisplayNameKey(priceId) {
    const subInfo = getSubscriptionType(priceId);
    if (!subInfo) return null;

    const displayNameKeys = {
        premium: {
            monthly: "account_subscriptions_current_mensual",
            annually: "account_subscriptions_current_annually",
            sixMonths: "account_subscriptions_current_sixMonths",
            africaMonthly: "account_subscriptions_current_africa_monthly",
            africaCommitted: "account_subscriptions_current_africa_committed",
        },
        ultra: {
            monthly: "account_subscriptions_current_ultra_mensual",
        },
        essential: {
            monthly: "account_subscriptions_current_essential_mensual",
            annually: "account_subscriptions_current_essential_annually",
        },
    };

    return displayNameKeys[subInfo.type]?.[subInfo.cycle] || null;
}

/**
 * Vérifie si un abonnement peut être résilié en fonction de son engagement.
 *
 * @param {string} priceId - Le price ID Stripe
 * @param {number} billingCycleAnchor - Le timestamp Unix du début de l'abonnement
 * @param {number} pausedMonths - Le décalage de l'engagement, en mois
 *   (getEngagementShiftMonths) : les mois de pause le repoussent, les
 *   mensualités réglées avant une reprise anticipée le raccourcissent
 * @returns {{ canCancel: boolean, engagementEndDate: number | null, remainingDays: number | null }}
 */
export function getEngagementInfo(priceId, billingCycleAnchor, pausedMonths = 0) {
    const info = findPriceInfo(priceId);

    // Si le price ID n'est pas trouvé, on autorise la résiliation par défaut
    if (!info) {
        return {
            canCancel: true,
            engagementEndDate: null,
            remainingDays: null,
        };
    }

    // Pas d'engagement = résiliation autorisée
    if (!info.engagement) {
        return {
            canCancel: true,
            engagementEndDate: null,
            remainingDays: null,
        };
    }

    // Calculer la date de fin d'engagement : en mois calendaires quand la
    // durée en est une, pour tomber pile sur une fin de période Stripe (365
    // jours finissent un jour trop tôt quand l'année compte un 29 février).
    const engagementMonths = ENGAGEMENT_MONTHS[info.engagement];
    const engagementEndDate = engagementMonths
        ? addBillingMonths(billingCycleAnchor, engagementMonths + pausedMonths)
        : billingCycleAnchor + info.engagement + pausedMonths * 2592000;
    const now = Math.floor(Date.now() / 1000);

    // Si l'engagement est terminé, résiliation autorisée
    if (now >= engagementEndDate) {
        return { canCancel: true, engagementEndDate, remainingDays: 0 };
    }

    // Engagement en cours
    const remainingSeconds = engagementEndDate - now;
    const remainingDays = Math.ceil(remainingSeconds / 86400);

    return {
        canCancel: false,
        engagementEndDate,
        remainingDays,
    };
}

/**
 * Raccourci pour vérifier si un abonnement peut être résilié.
 *
 * @param {string} priceId - Le price ID Stripe
 * @param {number} billingCycleAnchor - Le timestamp Unix du début de l'abonnement
 * @returns {boolean}
 */
export function canCancelSubscription(priceId, billingCycleAnchor) {
    return getEngagementInfo(priceId, billingCycleAnchor).canCancel;
}

/** Les durées d'engagement qui se comptent en mois de facturation. */
const ENGAGEMENT_MONTHS = {
    [ENGAGEMENT_PERIODS.ONE_MONTH]: 1,
    [ENGAGEMENT_PERIODS.THREE_MONTHS]: 3,
    [ENGAGEMENT_PERIODS.SIX_MONTHS]: 6,
    [ENGAGEMENT_PERIODS.TWELVE_MONTHS]: 12,
};

/**
 * Avance un timestamp de quelques mois comme Stripe avance ses périodes
 * mensuelles : même jour et même heure (UTC) que l'ancre, ramenés au dernier
 * jour quand le mois est plus court (une ancre au 31 janvier tombe le 28 ou
 * le 29 février). Le calcul part toujours de l'ancre, comme Stripe.
 */
export function addBillingMonths(anchor, months) {
    const start = new Date(anchor * 1000);
    const target = new Date(
        Date.UTC(
            start.getUTCFullYear(),
            start.getUTCMonth() + months,
            1,
            start.getUTCHours(),
            start.getUTCMinutes(),
            start.getUTCSeconds(),
        ),
    );
    const lastDay = new Date(
        Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
    ).getUTCDate();
    target.setUTCDate(Math.min(start.getUTCDate(), lastDay));
    return Math.floor(target.getTime() / 1000);
}

/** La durée d'engagement d'un prix, en mois (null s'il n'en a pas). */
export function getEngagementMonths(priceId) {
    const info = findPriceInfo(priceId);
    return ENGAGEMENT_MONTHS[info?.engagement] || null;
}

/**
 * Métadonnée posée sur un abonnement résilié pendant son engagement.
 *
 * Elle distingue notre `cancel_at` (fin d'engagement demandée par l'abonné)
 * de celui des anciens paiements échelonnés, posé dès leur création.
 */
export const ENGAGEMENT_CANCEL_MARKER = "believemy_cancel_at_engagement_end";

/**
 * Une résiliation est-elle programmée sur cet abonnement ?
 *
 * Deux formes chez Stripe : `cancel_at_period_end` (fin de la période en
 * cours, le cas général) et `cancel_at` à la fin de l'engagement, marqué par
 * ENGAGEMENT_CANCEL_MARKER. Tout ce qui lisait `cancel_at_period_end` seul
 * doit passer par ici, sinon une résiliation sous engagement reste invisible.
 */
export function isCancellationScheduled(subscription) {
    if (!subscription) return false;
    if (subscription.cancel_at_period_end === true) return true;
    if (isReturningToPro(subscription)) return true;
    return Boolean(
        subscription.cancel_at &&
            subscription.metadata?.[ENGAGEMENT_CANCEL_MARKER],
    );
}

/** Le jour où prend fin un abonnement dont la résiliation est programmée. */
export function getScheduledEndDate(subscription) {
    return (
        subscription?.cancel_at ||
        (isReturningToPro(subscription)
            ? Number(subscription.metadata[CARRIED_ENGAGEMENT_END_KEY])
            : null) ||
        subscription?.current_period_end ||
        null
    );
}

// ============================================================================
// CHANGEMENTS D'OFFRE VERS LE HAUT
// ============================================================================

/**
 * Les deux passages vers une offre supérieure, faits depuis le compte sans
 * repasser par le tunnel : Believemy Plus -> Pro et Believemy Pro -> Founder.
 * `marker` est la valeur posée dans la métadonnée `believemy_upgrade` de
 * l'abonnement le temps du passage ; Stripe la recopie sur la facture, où le
 * webhook la lit pour ouvrir le nouvel accès.
 */
export const UPGRADE_MARKERS = {
    essentialToPremium: "essential_to_premium",
    premiumToUltra: "premium_to_ultra",
};

/** La métadonnée porte-t-elle un passage vers une offre supérieure ? */
export function isUpgradeMarker(marker) {
    return Object.values(UPGRADE_MARKERS).includes(marker);
}

/**
 * Vers quoi cet abonnement peut-il monter ?
 *
 * Plus monte vers le Pro du même cycle, sur un cycle neuf. Pro monte vers
 * Founder (mensuel). Sous engagement, l'échéance du Pro est conservée : la
 * fin de l'engagement reste une fin de période, et l'abonnement peut y
 * revenir sans rien proratiser (voir getCarriedEngagement).
 *
 * @returns {{ from: string, to: string, marker: string, priceId: string | undefined, cycle: string, keepBillingAnchor: boolean, engagementEndDate: number | null } | null}
 */
export function getUpgradePath(subscription) {
    const priceId =
        subscription?.items?.data?.[0]?.price?.id || subscription?.plan?.id;
    const info = findPriceInfo(priceId);
    if (!info) return null;

    if (info.type === "essential") {
        const cycle = info.cycle === "annually" ? "annually" : "monthly";
        return {
            from: "essential",
            to: "premium",
            marker: UPGRADE_MARKERS.essentialToPremium,
            priceId: getCurrentPriceId("premium", cycle),
            cycle,
            keepBillingAnchor: false,
            engagementEndDate: null,
        };
    }

    if (info.type === "premium") {
        const engagement = getCancellationEffectiveDate(subscription);
        return {
            from: "premium",
            to: "ultra",
            marker: UPGRADE_MARKERS.premiumToUltra,
            priceId: getCurrentPriceId("ultra", "monthly"),
            cycle: "monthly",
            keepBillingAnchor: engagement.engaged,
            engagementEndDate: engagement.engaged ? engagement.date : null,
        };
    }

    return null;
}

/**
 * Métadonnée posée sur un Founder né d'un Pro sous engagement : le prix Pro
 * d'origine. L'engagement n'est pas effacé par le passage à Founder, il est
 * mis de côté ; l'ancre de facturation n'ayant pas bougé, ce prix et l'ancre
 * suffisent à retrouver sa date de fin.
 */
export const CARRIED_ENGAGEMENT_PRICE_KEY = "believemy_engagement_price";

/** La fin de l'engagement, notée quand le retour à Pro est programmé. */
export const CARRIED_ENGAGEMENT_END_KEY = "believemy_engagement_until";

/**
 * Le motif écrit par le membre quand il résilie un Founder qui repasse à Pro.
 * Stripe refuse `cancellation_details` tant que l'abonnement n'a pas de date
 * de fin, et il n'en a une qu'au retour à Pro : le motif attend ici, et le
 * webhook le recopie à ce moment-là.
 */
export const CANCEL_REASON_KEY = "believemy_cancel_reason";

/**
 * Valeur de `believemy_downgrade` quand un Founder résilié repasse à Pro
 * jusqu'à la fin de son engagement d'origine.
 */
export const RETURN_TO_PRO_MARKER = "ultra_to_premium";

/**
 * L'engagement Pro qu'un Founder a emporté avec lui, s'il court encore
 * au-delà de la période en cours. Résilier Founder ne libère pas de cet
 * engagement : l'abonnement repasse à Pro jusqu'à son terme.
 *
 * @returns {{ priceId: string, endDate: number, months: number | null } | null}
 */
export function getCarriedEngagement(subscription) {
    const proPriceId = subscription?.metadata?.[CARRIED_ENGAGEMENT_PRICE_KEY];
    if (!proPriceId) return null;
    if (!isPriceOfType(subscription.plan?.id, "ultra")) return null;

    const engagement = getEngagementInfo(
        proPriceId,
        subscription.billing_cycle_anchor,
        getEngagementShiftMonths(subscription),
    );
    const periodEnd = subscription.current_period_end || null;
    if (
        engagement.canCancel ||
        !engagement.engagementEndDate ||
        !periodEnd ||
        engagement.engagementEndDate <= periodEnd
    ) {
        return null;
    }
    return {
        priceId: proPriceId,
        endDate: engagement.engagementEndDate,
        months: getEngagementMonths(proPriceId),
    };
}

/**
 * Vers quel Pro peut redescendre un Founder qui préfère rester plutôt que
 * résilier (/api/user/downgrade-to-pro) ?
 *
 * S'il avait emporté un engagement Pro en montant, une seule réponse : il
 * retrouve ce prix et son engagement continue jusqu'à son terme (`carried`).
 * Sinon les deux formules du catalogue lui sont proposées : l'année en douze
 * mensualités (`committed`, un NOUVEL engagement qui démarre le jour de la
 * bascule) et le mensuel sans engagement (`monthly`).
 *
 * @returns {Array<{ formula: "carried" | "committed" | "monthly", priceId: string | undefined, engagementEndDate: number | null, engagementMonths: number | null }>}
 */
export function getFounderDowngradeOptions(subscription) {
    const carried = getCarriedEngagement(subscription);
    if (carried) {
        return [
            {
                formula: "carried",
                priceId: carried.priceId,
                engagementEndDate: carried.endDate,
                engagementMonths: null,
            },
        ];
    }
    const options = [];
    const committedPriceId = getCurrentPriceId("premium", "annually");
    const committedMonths = getEngagementMonths(committedPriceId);
    // Seulement si l'annuel du moment se paie bien chaque mois sous
    // engagement : un annuel réglé d'un coup n'a pas sa place ici.
    if (committedPriceId && committedMonths) {
        options.push({
            formula: "committed",
            priceId: committedPriceId,
            engagementEndDate: null,
            engagementMonths: committedMonths,
        });
    }
    options.push({
        formula: "monthly",
        priceId: getCurrentPriceId("premium", "monthly"),
        engagementEndDate: null,
        engagementMonths: null,
    });
    return options;
}

/**
 * Un Founder résilié sous engagement : il garde Founder jusqu'à la fin de la
 * période payée, repasse à Pro, et l'abonnement s'arrête à la fin de
 * l'engagement. Tant qu'il est encore sur Founder, Stripe ne porte aucun
 * `cancel_at` (il n'apparaît qu'à la dernière phase) : c'est ce marqueur qui
 * dit que la fin est programmée.
 */
export function isReturningToPro(subscription) {
    return Boolean(
        subscription?.metadata?.believemy_downgrade === RETURN_TO_PRO_MARKER &&
            Number(subscription.metadata[CARRIED_ENGAGEMENT_END_KEY]) > 0,
    );
}

/**
 * Le jour où prendrait effet une résiliation demandée maintenant.
 *
 * Sous engagement, c'est la fin de l'engagement : les mensualités restantes
 * sont dues, et l'abonnement ne doit surtout pas s'arrêter à la fin du mois
 * en cours. Sinon, c'est la fin de la période en cours. Dans la dernière
 * période d'engagement, les deux dates se confondent : on retombe sur le cas
 * général.
 *
 * Un Founder né d'un Pro sous engagement a emporté cet engagement avec lui :
 * résilié, il repasse à Pro à la fin de la période payée et l'abonnement
 * s'arrête à la fin de l'engagement (`returnsToPro`, voir
 * getCarriedEngagement).
 *
 * @returns {{ date: number | null, engaged: boolean, months: number | null, returnsToPro?: boolean, proPriceId?: string, founderEndDate?: number }}
 */
export function getCancellationEffectiveDate(subscription) {
    const periodEnd = subscription?.current_period_end || null;
    const carried = getCarriedEngagement(subscription);
    if (carried) {
        return {
            date: carried.endDate,
            engaged: true,
            months: carried.months,
            returnsToPro: true,
            proPriceId: carried.priceId,
            founderEndDate: periodEnd,
        };
    }
    const engagement = getEngagementInfo(
        subscription?.plan?.id,
        subscription?.billing_cycle_anchor,
        getEngagementShiftMonths(subscription),
    );

    if (
        !engagement.canCancel &&
        engagement.engagementEndDate &&
        periodEnd &&
        engagement.engagementEndDate > periodEnd
    ) {
        const info = findPriceInfo(subscription.plan.id);
        return {
            date: engagement.engagementEndDate,
            engaged: true,
            months: ENGAGEMENT_MONTHS[info?.engagement] || null,
        };
    }

    return { date: periodEnd, engaged: false, months: null };
}

// ============================================================================
// PAUSE
// ============================================================================

/**
 * Une pause dure de un à trois mois : pendant ce temps aucune mensualité n'est
 * prélevée (Stripe annule les factures, `pause_collection`), l'accès est
 * suspendu, et un engagement en cours est repoussé d'autant. Une seule pause
 * par abonnement et par compte. Les règles vivent dans lib/subscriptionPause.js ;
 * ces clés sont ici parce que le calcul de l'engagement les lit.
 */
export const PAUSE_MONTHS_KEY = "believemy_pause_months";
/** Première échéance sautée (la fin de la période payée au moment de la demande). */
export const PAUSE_FROM_KEY = "believemy_pause_from";
/** L'échéance où les prélèvements reprennent. */
export const PAUSE_UNTIL_KEY = "believemy_pause_until";

/**
 * Les mensualités d'engagement déjà réglées quand une reprise anticipée a
 * remis l'ancre de facturation à zéro (/api/user/resume-subscription). La
 * durée d'un engagement se compte depuis l'ancre : sans ce nombre, reprendre
 * une pause ferait repartir l'engagement pour douze mois.
 */
export const ENGAGEMENT_PAID_KEY = "believemy_engagement_paid_months";

/**
 * De combien de mois la fin de l'engagement est-elle décalée par rapport à
 * « ancre + durée » ? Les mois de pause la repoussent ; les mensualités
 * réglées avant une remise à zéro de l'ancre la rapprochent.
 */
export function getEngagementShiftMonths(subscription) {
    const paid = Number(subscription?.metadata?.[ENGAGEMENT_PAID_KEY]);
    return (
        getPauseMonths(subscription) -
        (Number.isFinite(paid) && paid > 0 ? paid : 0)
    );
}

/** Les mois de pause pris sur cet abonnement, en cours ou passés. */
export function getPauseMonths(subscription) {
    const months = Number(subscription?.metadata?.[PAUSE_MONTHS_KEY]);
    return Number.isFinite(months) && months > 0 ? months : 0;
}
